import { useQuery } from "@apollo/client";
import { withErrorBoundary } from "@sentry/react";
import {
  GetAskAiMeetingEmbedQueryQuery,
  GetAskAiMeetingEmbedQueryQueryVariables,
  SuggestedTopicItemNode,
} from "types/graphql-schema";

import ErrorFallback from "@components/error-fallback/error-fallback";

import getAskAiMeetingEmbedQuery from "../graphql/get-ask-ai-meeting-embed-query";
import AskAIEmbedSuggestedTopicForMeeting from "./ask-ai-embed-suggested-topic";
import { askAiEmbedItemClassName } from "./ask-ai-embeds-constants";

const AskAIPlaceholderSuggestedTopicComponent = ({
  meetingId,
  topics,
}: {
  meetingId?: number | null;
  topics?: SuggestedTopicItemNode[] | null;
}) => {
  const { data } = useQuery<
    GetAskAiMeetingEmbedQueryQuery,
    GetAskAiMeetingEmbedQueryQueryVariables
  >(getAskAiMeetingEmbedQuery, {
    variables: { meetingId: meetingId || 0 },
    skip: !meetingId,
  });
  const meeting = data?.meeting;
  return (
    <>
      <div className={askAiEmbedItemClassName} key={meetingId}>
        {meeting ? (
          <AskAIEmbedSuggestedTopicForMeeting
            meeting={meeting}
            suggestedTopics={topics || []}
          />
        ) : (
          <div className="flex flex-col gap-4 w-full">
            <div className="flex gap-3 items-center">
              <div className="flex-1">
                <div className="text-gray-500 bg-gray-100 animate-pulse rounded-md h-6 max-w-48" />
              </div>
              <div className="text-gray-500 bg-gray-100 animate-pulse rounded-md h-6 w-30" />
              <div className="text-gray-500 bg-gray-100 animate-pulse rounded-md h-6 w-24" />
            </div>
            <div className="flex gap-1">
              <div className="w-30 tf-body-sm-light">Details</div>
              <div className="">
                <div className="text-gray-500 bg-gray-100 animate-pulse rounded-md h-6 w-24" />
              </div>
            </div>
            <div className="flex gap-1">
              <div className="w-30 tf-body-sm-light">Participants</div>
              <div className="flex items-center gap-1">
                <div className="text-gray-500 bg-gray-100 animate-pulse rounded-full h-5 w-5" />
                <div className="text-gray-500 bg-gray-100 animate-pulse rounded-full h-5 w-5" />
              </div>
            </div>
            <div className="flex gap-1">
              <div className="w-30 tf-body-sm-light">Topics</div>
              <div className="">
                {(topics || []).map((topic, idx) => {
                  const hasNotes =
                    topic.notes &&
                    (Array.isArray(topic.notes)
                      ? topic.notes.length > 0
                      : false);
                  return (
                    <div key={idx}>
                      <div>{topic.title}</div>
                      {hasNotes && (
                        <div className="text-sm text-gray-500">
                          {Array.isArray(topic.notes)
                            ? `${topic.notes.length} note${
                                topic.notes.length > 1 ? "s" : ""
                              }`
                            : "with notes"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const AskAIPlaceholderSuggestedTopicWithSentry = withErrorBoundary(
  AskAIPlaceholderSuggestedTopicComponent,
  {
    fallback: ErrorFallback,
  },
);

export default AskAIPlaceholderSuggestedTopicWithSentry;
